import React from 'react';
import { Feature } from '@tencent/workbench';
import BaseToolbarFeature from '@framework/workbench/common/feature/base-toolbar-feature';
import { config } from './config';
import { TransitionType } from '@src/slide/domain/ui-model/element-modeles/transition/constants';
import get from 'lodash/get';
import { ITransitionEffect } from '@framework/workbench/ui/transition/transition-effect-option/transition-effect-list';
import {
    transitionEffectMapping,
    TRULY_SYMBOL,
} from '@framework/workbench/ui/transition/transition-effect-option/effect-mapping';
import { SideDirection } from '@src/slide/domain/ui-model/element-modeles/transition/direction/side-direction';
import {
    EightDirectionType,
    InOutDirectionType,
    SideDirectionType,
    SwitchDirectionType,
} from '@src/slide/domain/ui-model/element-modeles/transition/direction/constants';
import { EightDirection } from '@src/slide/domain/ui-model/element-modeles/transition/direction/eight-direction';
import { SwitchDirection } from '@src/slide/domain/ui-model/element-modeles/transition/direction/switch-direction';
import { InOutDirection } from '@src/slide/domain/ui-model/element-modeles/transition/direction/in-out-direction';
import { transaction } from '@src/slide/domain/slide-app';
import { ITransitionService } from '@src/slide/application/service/transition-service/transition-service-interface';

@Feature()
export class TransitionEffectOptionFeature extends BaseToolbarFeature {
    public id = config.id;
    public config = config;
    private previewTimer: NodeJS.Timeout;

    public constructor() {
        super();
    }

    public getIcon() {
        return <img src={this.getTransitionEffectIcon()} alt=""/>;
    }

    public getTransitionEffect() {
        const mapping = transitionEffectMapping[this.currentTransitionType];
        if (!mapping) return;

        // 无效果选项的
        if (mapping?.disable) {
            return mapping as ITransitionEffect;
        }

        // 特殊类型的，有选项但属于其他type的一个options的，如newsflash
        if (mapping?.data) {
            return mapping.data;
        }

        const value = get(this.currentSlideTransition, mapping.conditionKey!);

        if (mapping.bool) {
            const key = !!value;
            return mapping.conditions?.[key as any];
        }

        return mapping?.conditions?.[value];
    }

    public autoPreview(service: ITransitionService) {
        if (service.autoPreviewState) {
            clearTimeout(this.previewTimer);

            // 更新model之后会触发 slide-wrapper的update，里面会reset transition-layer
            // 这里延迟预览，保证以新的layer context进行preview
            this.previewTimer = setTimeout(() => {
                service.preview();
            }, 100);
        }
    }

    public updateThroughBlack(name: string) {
        const value = this.getEffectOptionValue(name);

        if (value) {
            this.transitionService?.then((service) => {
                service.updateTransitionEffect({ throughBlack: value === TRULY_SYMBOL } as any);
                this.autoPreview(service);
            });
        }
    }

    public updateSideDirection(name: string) {
        const value = this.getEffectOptionValue(name);

        if (value) {
            this.transitionService?.then((service) => {
                service.updateTransitionEffect({
                    direction: new SideDirection(value as SideDirectionType),
                } as any);
                this.autoPreview(service);
            });
        }
    }

    public updateEightDirection(name: string) {
        const value = this.getEffectOptionValue(name);

        if (value) {
            this.transitionService?.then((service) => {
                service.updateTransitionEffect({
                    direction: new EightDirection(value as EightDirectionType),
                } as any);
                this.autoPreview(service);
            });
        }
    }

    public updateInOutDirection(name: string) {
        transaction(() => {
            // 如果是newsflash，虽然处于一个menu，但是type不同，这里要制定warp类型
            const value = this.getEffectOptionValue(name, TransitionType.WARP);
            if (this.currentTransitionType !== TransitionType.WARP) {
                this.transitionService?.then((service) => {
                    service.updateTransitionType(TransitionType.WARP);
                });
            }

            if (value) {
                this.transitionService?.then((service) => {
                    service.updateTransitionEffect({
                        direction: new InOutDirection(value as InOutDirectionType),
                    } as any);

                    this.autoPreview(service);
                });
            }
        });
    }

    public updateSwitchDirection(name: string) {
        const value = this.getEffectOptionValue(name);

        if (value) {
            this.transitionService?.then((service) => {
                service.updateTransitionEffect({
                    direction: new SwitchDirection(value as SwitchDirectionType),
                } as any);

                this.autoPreview(service);
            });
        }
    }

    public updateNewsflash() {
        this.transitionService?.then((service) => {
            service.updateTransitionType(TransitionType.NEWSFLASH);
            this.autoPreview(service);
        });
    }

    public getCustomDisabledState() {
        const effect = this.getTransitionEffect();

        return !!(effect as any).disable;
    }

    public get currentTransitionType() {
        return this.currentSlideTransition?.type || TransitionType.EMPTY;
    }

    private getEffectOptionValue(name: string, type?: TransitionType) {
        const mapping = transitionEffectMapping[type ?? this.currentTransitionType];
        const conditions = mapping?.conditions;
        if (!mapping || !conditions) return;

        // uiData effect的value在transitionEffectMapping作为条件索引
        return Object.keys(conditions).find((key) => {
            const effectOption = conditions[key];
            return effectOption.id === name;
        });
    }

    private getTransitionEffectIcon(): string {
        return this.getTransitionEffect()?.icon || '';
    }

    private get transitionService() {
        return this.getAppService().editableServiceLayer?.transitionService?.promise();
    }

    private get currentSlideTransition() {
        return this.getAppService().viewableServiceLayer.selectionService.currentSlide?.transition;
    }
}
